import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { referralService } from 'api/referral';

interface ReferralState {
  campaigns: any[];
  referrals: any[];
  payoutSummary: {
    totalReferrals: number;
    totalClaimed: string;
    available: string;
    totalEarned: string;
  } | null;
  totalCampaigns: number;
  currentCampaignPage: number;
  campaignLimit: number;
  totalReferrals: number;
  currentReferralPage: number;
  referralLimit: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: ReferralState = {
  campaigns: [],
  referrals: [],
  payoutSummary: null,
  totalCampaigns: 0,
  currentCampaignPage: 1,
  campaignLimit: 20,
  totalReferrals: 0,
  currentReferralPage: 1,
  referralLimit: 20,
  isLoading: false,
  error: null,
};

export const createCampaign = createAsyncThunk(
  'referral/createCampaign',
  async (payload: { name: string; code: string }, { rejectWithValue }) => {
    try {
      const response = await referralService.createCampaign(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create campaign');
    }
  },
);

export const fetchCampaigns = createAsyncThunk(
  'referral/fetchCampaigns',
  async (params: { limit: number; page: number }, { rejectWithValue }) => {
    try {
      const response = await referralService.getCampaigns(params);
      return response?.data ?? null;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch campaigns');
    }
  },
);

export const fetchReferrals = createAsyncThunk(
  'referral/fetchReferrals',
  async (params: { limit: number; page: number } | undefined, { rejectWithValue }) => {
    try {
      const response = await referralService.getReferrals(params);
      return response?.data ?? null;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch referrals');
    }
  },
);

export const fetchPayoutSummary = createAsyncThunk(
  'referral/fetchPayoutSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await referralService.getPayoutSummary();
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payout summary');
    }
  },
);

const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {
    clearReferralError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createCampaign.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCampaign.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCampaigns.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.isLoading = false;
        state.campaigns = action.payload?.docs ?? [];
        state.totalCampaigns = action.payload?.total ?? 0;
        state.currentCampaignPage = action.payload?.page ?? 1;
        state.campaignLimit = action.payload?.limit ?? 20;
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchReferrals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReferrals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.referrals = action.payload?.docs ?? [];
        state.totalReferrals = action.payload?.total ?? 0;
        state.currentReferralPage = action.payload?.page ?? 1;
        state.referralLimit = action.payload?.limit ?? 20;
      })
      .addCase(fetchReferrals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPayoutSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPayoutSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payoutSummary = action.payload;
      })
      .addCase(fetchPayoutSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearReferralError } = referralSlice.actions;
export default referralSlice.reducer;
